import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import DocViewer from "@/components/DocViewer";
import { CONTACTO_GENERAL, waConTexto } from "@/components/Contactanos";
import { ShieldCheck, CheckCircle2, Clock3, Eye, MessageCircle, X, Send } from "lucide-react";

export const TIPOS_AFILIACION = ["EPS", "Caja de compensación", "ARL"];
const slug = (t) => t.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/\s+/g, "-");

function Reportar({ afiliaciones, empleado, rol, onClose }) {
  const [tipo, setTipo] = useState(afiliaciones[0]?.tipo || TIPOS_AFILIACION[0]);
  const [texto, setTexto] = useState("");
  const a = afiliaciones.find((x) => x.tipo === tipo);
  const enviar = () => {
    const msg = [
      "*Reporte de inconsistencia en afiliación – Roge*",
      `Reportado por: ${rol}`,
      `Empleado: ${empleado.nombre}${empleado.documento ? ` · Doc. ${empleado.documento}` : ""}`,
      empleado.empresa ? `Empresa usuaria: ${empleado.empresa}` : null,
      `Afiliación: ${tipo}${a?.entidad ? ` (${a.entidad})` : ""}${a?.numero ? ` · N.º ${a.numero}` : ""}`,
      `Problema: ${texto.trim()}`,
    ].filter(Boolean).join("\n");
    window.open(waConTexto(CONTACTO_GENERAL, msg), "_blank", "noopener");
    onClose();
  };
  return (
    <div className="fixed inset-0 z-[70] flex items-end justify-center bg-black/50 sm:items-center sm:p-4" data-testid="modal-reportar-afiliacion">
      <div className="w-full max-w-md rounded-t-3xl bg-white p-6 shadow-2xl sm:rounded-2xl">
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <p className="font-display text-base font-semibold text-slate-900">Reportar inconsistencia a HUMAN</p>
            <p className="mt-1 text-xs text-slate-500">El reporte se envía por WhatsApp a HUMAN al {CONTACTO_GENERAL.numero} (certificaciones, actualizaciones y novedades).</p>
          </div>
          <button onClick={onClose} className="rounded-full p-1 text-slate-400 hover:bg-slate-100" data-testid="reportar-cerrar" aria-label="Cerrar"><X className="h-5 w-5" /></button>
        </div>
        <label className="mb-1 block text-xs font-medium text-slate-500">Afiliación afectada</label>
        <div className="mb-4 grid grid-cols-3 gap-2">
          {TIPOS_AFILIACION.map((t) => (
            <button key={t} type="button" onClick={() => setTipo(t)} data-testid={`reportar-tipo-${slug(t)}`}
              className={`rounded-lg border px-2 py-2 text-xs font-semibold ${tipo === t ? "border-vino-600 bg-vino-50 text-vino-800" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>{t}</button>
          ))}
        </div>
        <label className="mb-1 block text-xs font-medium text-slate-500">Describe el problema *</label>
        <textarea value={texto} onChange={(e) => setTexto(e.target.value)} rows={4} maxLength={800} data-testid="reportar-descripcion"
          placeholder="Ej.: La EPS que aparece no es a la que estoy afiliado / el soporte tiene otro número de documento."
          className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-vino-500 focus:outline-none focus:ring-2 focus:ring-vino-100" />
        <button onClick={enviar} disabled={texto.trim().length < 5} data-testid="reportar-enviar"
          className="mt-4 flex w-full items-center justify-center gap-2 rounded-lg bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50">
          <Send className="h-4 w-4" /> Enviar por WhatsApp
        </button>
      </div>
    </div>
  );
}

function Tarjeta({ tipo, a, onVer }) {
  const ok = a?.estado === "registrada";
  return (
    <div className={`rounded-xl border p-4 ${ok ? "border-emerald-200 bg-emerald-50/50" : "border-slate-200 bg-white"}`} data-testid={`afil-card-${slug(tipo)}`}>
      <div className="flex items-start justify-between gap-2">
        <p className="font-semibold text-slate-800">{tipo}</p>
        {ok ? <span className="inline-flex shrink-0 items-center gap-1 text-xs font-semibold text-emerald-700"><CheckCircle2 className="h-3.5 w-3.5" /> Registrada</span>
          : <span className="inline-flex shrink-0 items-center gap-1 text-xs font-semibold text-amber-700"><Clock3 className="h-3.5 w-3.5" /> Pendiente</span>}
      </div>
      <p className="mt-1 break-words text-sm text-slate-600">{a?.entidad || "Entidad sin registrar"}</p>
      {a?.numero && <p className="text-xs text-slate-400">N.º {a.numero}</p>}
      {a?.evidencia ? (
        <button onClick={() => onVer(a)} data-testid={`afil-ver-${slug(tipo)}`} className="mt-3 flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-vino-700 hover:bg-vino-50">
          <Eye className="h-3.5 w-3.5" /> Ver soporte
        </button>
      ) : <p className="mt-3 text-xs text-slate-400">HUMAN aún no ha cargado el soporte.</p>}
    </div>
  );
}

// Afiliaciones (EPS, Caja, ARL) en solo lectura para el empleado o la empresa usuaria, con reporte por WhatsApp.
export function AfiliacionesConsulta({ base, empleado, rol }) {
  const [rows, setRows] = useState(undefined);
  const [extra, setExtra] = useState({});
  const [ver, setVer] = useState(null);
  const [reportar, setReportar] = useState(false);
  useEffect(() => { api.get(base).then((r) => { setRows(r.data.afiliaciones); setExtra(r.data.empleado || {}); }).catch(() => setRows([])); }, [base]);
  if (rows === undefined) return null;
  return (
    <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="afiliaciones-consulta">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="flex items-center gap-2 font-semibold text-slate-800"><ShieldCheck className="h-4 w-4 text-vino-700" /> Afiliaciones</h2>
          <p className="mt-0.5 text-xs text-slate-500">Soportes de afiliación a EPS, caja de compensación y ARL cargados por HUMAN.</p>
        </div>
        <button onClick={() => setReportar(true)} data-testid="btn-reportar-inconsistencia"
          className="flex shrink-0 items-center justify-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-800 hover:bg-emerald-100">
          <MessageCircle className="h-4 w-4" /> Reportar inconsistencia a HUMAN
        </button>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        {TIPOS_AFILIACION.map((t) => <Tarjeta key={t} tipo={t} a={rows.find((x) => x.tipo === t)} onVer={setVer} />)}
      </div>
      {ver && <DocViewer url={`${base}/${ver.id}/evidencia`} titulo={`Soporte de afiliación ${ver.tipo}`} subtitulo={ver.evidencia.original_filename} nombreArchivo={ver.evidencia.original_filename} onClose={() => setVer(null)} />}
      {reportar && <Reportar afiliaciones={rows} empleado={{ ...extra, ...Object.fromEntries(Object.entries(empleado).filter(([, v]) => v)) }} rol={rol} onClose={() => setReportar(false)} />}
    </section>
  );
}
